/* eslint-disable import/first */
/* eslint-disable react-hooks/exhaustive-deps */
import { IUserInfo, QueryUsersUseCase } from '@axelity/actasign-sic'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { Switch, Modal, message } from 'antd'
import NavBar from '../components/NavBar'
import AccountUser from '../components/AccountUser'
import PageHeader from '../components/PageHeader'
import UserList from '../components/UserList'
import useSignIn from '../hooks/useSignIn'
import {
  accountUsersState,
  currentAccountState,
  currentRolesState,
  showAccountUserDialogState,
  userInfoState,
  emailLinkDisabledState,
} from '../recoil/atoms'
import { getEmailLinkSetting, updateEmailLinkSetting } from '../services/emailLinkSettingService'
import SectionTitle from '../elements/SectionTitle'
import FieldTitle from '../elements/FieldTitle'

const SettingsAccountUsers = () => {
  const { t } = useTranslation(['settings', 'accountUsers'])

  const [noAccessToken] = useSignIn()

  const history = useHistory()

  const userInfo = useRecoilValue(userInfoState)
  const currentRoles = useRecoilValue(currentRolesState)

  const setShowDialog = useSetRecoilState(showAccountUserDialogState)
  const currentAccount = useRecoilValue(currentAccountState)

  const [users, setUsers] = useRecoilState(accountUsersState)
  const [emailLinkDisabled, setEmailLinkDisabled] = useRecoilState(emailLinkDisabledState)

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingToggleValue, setPendingToggleValue] = useState<boolean>(false)

  const handleNewUser = () => {
    setShowDialog(true)
  }

  const handleEmailLinkToggle = (checked: boolean) => {
    if (checked) {
      // User wants to disable email links - show confirmation
      setPendingToggleValue(true)
      setShowConfirmModal(true)
    } else {
      // User wants to enable email links - no confirmation needed
      updateEmailLinkSettingValue(false)
    }
  }

  const handleConfirmDisable = async () => {
    setShowConfirmModal(false)
    await updateEmailLinkSettingValue(true)
  }

  const handleCancelDisable = () => {
    setShowConfirmModal(false)
    setPendingToggleValue(false)
  }

  const updateEmailLinkSettingValue = async (disabled: boolean) => {
    if (!currentAccount?._id) return

    try {
      await updateEmailLinkSetting(currentAccount._id, disabled)
      setEmailLinkDisabled(disabled)
      message.success(
        disabled
          ? t('accountUsers:emailLinkSetting.disabledSuccess')
          : t('accountUsers:emailLinkSetting.enabledSuccess')
      )
    } catch (error) {
      console.error('Failed to update email link setting:', error)
      message.error(t('accountUsers:emailLinkSetting.updateError'))
    }
  }

  const fetchEmailLinkSetting = async () => {
    if (!currentAccount?._id) return

    try {
      const setting = await getEmailLinkSetting(currentAccount._id)
      setEmailLinkDisabled(setting.emailLinkDisabled)
    } catch (error) {
      console.error('Failed to fetch email link setting:', error)
    }
  }

  useEffect(() => {
    if (userInfo && currentAccount && currentRoles) {
      if (
        currentRoles.includes('PRIMARY-ADMIN') ||
        currentRoles.includes('USER-ADMIN') ||
        currentRoles.includes('MASTER-ADMIN')
      ) {
        new QueryUsersUseCase()
          .query({
            account: currentAccount._id,
          })
          .then((_users: IUserInfo[]) => {
            setUsers(_users)
          })
          .catch((error) => {
            console.error(error)
          })

        // Fetch email link setting
        fetchEmailLinkSetting()
      }
    }
  }, [userInfo, currentRoles, currentAccount])

  useEffect(() => {
    if (noAccessToken) history.push('/signin')
  }, [noAccessToken])

  useEffect(() => {
    if (currentRoles) {
      if (
        currentRoles.includes('PRIMARY-ADMIN') ||
        currentRoles.includes('USER-ADMIN') ||
        currentRoles.includes('MASTER-ADMIN')
      ) {
        // ok
      }
    }
  }, [currentRoles])

  return (
    <div>
      <NavBar />

      <PageHeader
        title={t('accountUsers:list.title')}
        pages={[
          {
            name: t('settings:accountSettings.name'),
          },
          {
            name: t('accountUsers:list.title'),
          },
        ]}
        buttons={[
          {
            href: '',
            label: t('accountUsers:list.newUser'),
            type: 'primary',
            onClick: handleNewUser,
          },
        ]}
      />

      <AccountUser />

      {/**
       * Email Link Settings
       */}
      <div className="container max-w-4xl mx-auto text-base bg-white border-2 border-gray-100 p-4 mt-4 shadow-sm rounded-md">
        <SectionTitle title={t('accountUsers:emailLinkSetting.title')} />

        <div className="mt-4">
          <FieldTitle title={t('accountUsers:emailLinkSetting.label')} />
          <div className="flex items-center mt-2">
            <Switch
              checked={emailLinkDisabled || false}
              onChange={handleEmailLinkToggle}
              checkedChildren={t('accountUsers:emailLinkSetting.disabled')}
              unCheckedChildren={t('accountUsers:emailLinkSetting.enabled')}
            />
            <span className="ml-4 text-gray-600">
              {emailLinkDisabled
                ? t('accountUsers:emailLinkSetting.description.disabled')
                : t('accountUsers:emailLinkSetting.description.enabled')}
            </span>
          </div>
        </div>
      </div>

      {/**
       * Users List
       */}
      <div className="container max-w-4xl mx-auto text-base bg-white p-4 mt-4 rounded-sm">
        <UserList users={users} />
      </div>

      {/**
       * Confirmation Modal
       */}
      <Modal
        title={t('accountUsers:emailLinkSetting.confirmModal.title')}
        open={showConfirmModal}
        onOk={handleConfirmDisable}
        onCancel={handleCancelDisable}
        okText={t('accountUsers:emailLinkSetting.confirmModal.confirm')}
        cancelText={t('accountUsers:emailLinkSetting.confirmModal.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>{t('accountUsers:emailLinkSetting.confirmModal.message')}</p>
        <p className="mt-2 text-gray-600">
          {t('accountUsers:emailLinkSetting.confirmModal.warning')}
        </p>
      </Modal>
    </div>
  )
}

export default SettingsAccountUsers
